/**
 * Help copy for the criteria panel, one entry per tooltip. The copy describes what the
 * runtime (whatif-server, publisher-pre-ping) actually does with each value. Check it against
 * the handler in whatif-server/src/main/java/com/whatif/criteria before changing it.
 *
 * Conventions that hold for every Include/Exclude list, so the entries below don't repeat them:
 * an unset mode or an empty list skips the check, matching ignores case, and a #tag stands for
 * every domain or affiliate carrying that tag.
 */
import { SUPPRESSION_TOOLTIPS } from './suppressionCopy';

export const HELP = {
  /* ---------------------------------------------------------------------------------- Users */
  compliance: 'Each switch adds one requirement the user must meet. Switches that are off add nothing.',
  gpc:
    'When on, users sending a Global Privacy Control signal from a state that enforces it do not see this offer. ' +
    'If a disclosure is present (offer explicitly states to user that their information will be passed/posted ' +
    'to an advertiser), then offer should be shown to GPC user.',
  age: 'Inclusive bounds, worked out from the date of birth. A user with no date of birth counts as age 0, so any Min Age excludes them.',
  excludeGender: 'Users whose gender matches are excluded. Users with no gender on file still pass.',
  excludeBrowserType: 'Excludes mobile or desktop sessions.',
  anuraScore:
    "Excludes sessions whose Anura fraud score matches. 'No Score' also excludes sessions that have not been scored yet.",
  requiredUserData:
    'Include: every selected field must be filled in on the user. Exclude: every selected field must be empty.',
  nullSourceNewUsers:
    'Users with no affiliate ID who were created in the last 24 hours. Include lets only those users through; Exclude blocks them.',
  aibList:
    'Only applies to affiliate 1006 traffic. The list code is read from characters 3–5 of aff_sub and must match a selected list exactly.',
  previousRegistrations:
    'Counts the user’s registrations over the last 30 days on each entry’s domains, then checks the count against Min/Max.',
  previousRegistrationDomains:
    "'All Domains' counts every domain; 'Current Domain' counts the domain of this session. Neither counts the registration happening now.",
  previousRegistrationCount:
    'Include passes when the count is between Min and Max (inclusive). Exclude fails when it is: with only Min set, at or above Min; with only Max set, at or below Max.',
  andOrLogic: 'All: every entry must pass. Any: at least one entry must pass. Pick a logic to start adding entries.',
  ipqs: 'Checks the IPQS fraud scores stored on the user against the thresholds below.',
  ipqsThreshold:
    'Fail the user when their IPQS score for this signal is above this number (IPQS scores run 0–100). Users with no score pass.',
  userIdLastDigit:
    'Matches the end of the user ID, so a value like 07 targets IDs ending in 07. Type and press Enter to add a value that is not in the list.',
  whatIfDomain: 'Matches the domain of the current session. A value matches any domain that contains it.',
  zipCodes:
    'The zip code on the user’s profile, not the one from IP lookup. Paste five-digit zips separated by spaces or new lines.',
  jobKeywords:
    'Matched against the job keyword the user searched for. One keyword per line. Include fails users who searched no keyword. Offers match exactly (ignoring case); offer actions allow small typos.',

  /* ------------------------------------------------------------------------------ Attribute */
  userAttributes:
    "Rules on the user's attributes. 'not_set' means the attribute is absent. Rules combine left to right in the order shown. Include requires the rules to match; Exclude fails when they do.",
  urlParameters:
    'Rules on URL parameters from this request or earlier in the session. Comparisons are case-sensitive. Rules combine left to right in the order shown.',
  userAgent:
    "Rules on the request's User-Agent header. 'contains' is a case-insensitive text match, so 'read aloud' catches 'Read Aloud'. Rules combine left to right in the order shown. Include requires the rules to match; Exclude fails when they do.",
  affiliateNames: "Matches the session's affiliate. '[Null]' stands for traffic with no affiliate at all.",
  affSubIds:
    "Rules on an affiliate's sub IDs, checked against both aff_sub and aff_sub2. An Include entry requires that affiliate and one of its sub IDs; an Exclude entry blocks the listed sub IDs for that affiliate.",
  affSubLogic:
    'All: every entry must pass. Any: at least one must pass. Independently: each entry only judges its own affiliate’s traffic and leaves other affiliates alone.',
  subIds: 'One sub ID per line. Leave blank with affiliate [Null] to target traffic with no affiliate.',
  domainAffiliates:
    'Per-domain affiliate rules. An entry only applies when the session domain is one of its domains; then its affiliate Include/Exclude decides.',
  emailDomains: 'Matches the part of the user’s email after the @ exactly.',
  landingPageGroups: 'Matches the landing page group of the session. Sessions without one skip this check.',
  landingPages: 'Matches the landing page of the session. Sessions without one skip this check.',
  criteriaQuestion:
    'Requires an answer to a question earlier in the session. With answers selected, the user must have answered with one of them (exact, case-sensitive).',
  criteriaQuestionAnswers: 'Leave empty to only require that the session has answered questions.',

  /* -------------------------------------------------------------------- Affiliate Quality Score */
  qualityScoreRanges:
    'The first range (top to bottom) whose score band and day filter match the affiliate is used; its cap and failure probability then apply. Affiliates with no quality score, or that match no range, fail.',
  qualityScoreMinMax: 'Inclusive bounds on the affiliate’s lead quality score. Blank leaves that side open.',
  qualityScoreCap: 'Most leads this row may pass, counted in Redis. Blank means no cap.',
  failureProbability: 'Chance a matching lead is failed at random, entered as a decimal (0.25 = 25%).',
  validMultipliers: 'The affiliate must have at least this many valid multipliers for the range to match.',
  dayFilter: 'Days the range applies on. Leave empty for every day.',
  bypassAffiliates:
    'Affiliates listed here skip the score ranges. Only this row’s cap and failure probability apply to them.',
  capReset: 'Stored with the configuration, but nothing reads it at runtime yet.',

  /* ------------------------------------------------------------------------------- Pre-ping */
  internalPrePings:
    'Suppresses users based on their own history: how often they saw or converted on this offer (and any additional ones) within a time window.',
  // ZET-5048: the suppression copy lives in suppressionCopy.ts. Max Limit has no entry here because its
  // wording follows the chosen Level (see maxLimitTooltip).
  suppressionType: SUPPRESSION_TOOLTIPS.type,
  suppressionAmount: SUPPRESSION_TOOLTIPS.amount,
  suppressionLevel: SUPPRESSION_TOOLTIPS.level,
  suppressionAdditional: 'History on these is counted together with this one.',
  externalPrePing:
    'Calls an outside URL before showing this offer. The user passes when the call answers with an accepted status and the handler (if any) returns true. No reply, or a timeout, fails.',
  prePingResultStored:
    'Saves the response body to the session when the pre-ping passes, so later steps can reuse it (as a token, say).',
  advancedPrePing: 'Runs this offer through the offer-level external pre-ping pipeline.',
  prePingMethod: 'GET sends no body. POST sends form fields, JSON sends application/json, XML sends text/xml.',
  prePingHandler:
    'JavaScript that reads the response and returns true to pass the user. Without one, any accepted status passes.',
  prePingUrl: 'Session variables in [{name}] form are substituted, e.g. [{ip_zip}].',
  prePingBody: 'Sent for POST, JSON and XML. Session variables in [{name}] form are substituted.',
  prePingTimeout: 'Milliseconds to wait for a reply. Blank uses the server default.',
  prePingHeader: "Written as 'Name: value'. Session variables in [{name}] form are substituted.",
  prePingHttpStatus: 'Extra status codes to accept besides 200, 201 and 202, separated by commas.',

  /* ------------------------------------------------------------------------- Caps & Restrictions */
  dailyLimit: 'Question and Banner offers only. The offer stops showing once today’s count reaches the limit.',
  dailyLimitAnswers: 'Only these answers count toward the daily limit.',
  capInterval:
    'Caps how many times this can pass in each interval. Lifetime never resets. A blank amount with Daily blocks everyone.',
  currentCap: 'The live count from Redis. N/A means nothing has been counted for this interval yet.',
  capDependencies:
    'Stops this offer automatically based on the cap status of other offers and actions. Example: if Offer A is ' +
    "a pre-qualifying question for Offer B, and Offer B's action has a daily cap of 100, make Offer A depend on " +
    'that action. When it hits 100 for the day, Offer A stops showing.',
  capGroup:
    'Max successful posts per day across every offer action sharing this Cap Group. Failed posts do not count. The counter resets daily.',
  dayParting:
    'Available: no time limit. Unavailable: never shown that day. Partially Restricted: shown only between Start and End. A per-day cap counts in Eastern time.',

  /* ------------------------------------------------------------------------------- Analysis */
  randomFailure:
    'Fails a share of the users who match the condition, at random. Every row is checked; any row that triggers fails the user.',
  randomFailureProbability: 'Share of the users matching the condition to fail at random, as a percentage.',
  randomFailureCondition: 'Which sessions this row applies to. Leave empty to apply it to everyone.',
} as const;
